/**
 * PatioForge motion — the theme's single scroll-reveal + micro-interaction
 * controller. No libraries. Loaded once, deferred, from layout/theme.liquid.
 *
 * Contract
 *   - Markup ships VISIBLE. Hidden states are gated behind .fp-motion-ready on
 *     <html>, which only this file adds, so no-JS / failed-JS shows everything.
 *   - Elements already in (or near) the viewport at init are marked revealed
 *     BEFORE .fp-motion-ready is set, so above-the-fold content — including the
 *     LCP hero — never starts transparent and is never delayed.
 *   - Only opacity / transform / clip-path animate. Nothing reflows.
 *   - prefers-reduced-motion: reduce short-circuits every code path here and is
 *     also neutralised in fp-motion.css.
 *
 * Authoring contract (liquid):
 *   <div data-fp-anim="fade-up">…</div>          single element reveal
 *   <div data-fp-anim data-fp-stagger>…</div>    reveal + stagger direct children
 * Variants: fade-up (default) | fade | scale-in | mask | rise (section heads)
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ */
  /* Legacy .fp-reveal sections (kept: fp-hero/story/lifestyle contract)  */
  /* ------------------------------------------------------------------ */

  var AUTO_SECTIONS = [
    {
      root: '.fp-lifestyle',
      child: '.fp-lifestyle__intro',
      stagger: '.fp-lifestyle__grid',
    },
    {
      root: '.fp-story',
      child: null,
      stagger: '.fp-story__bullets, .fp-story__rail',
    },
  ];

  /* ------------------------------------------------------------------ */
  /* Storefront-wide registry — one vocabulary, every page type.         */
  /*   sel     : elements to reveal                                     */
  /*   anim    : primitive variant                                      */
  /*   stagger : true = stagger this element's direct children          */
  /* ------------------------------------------------------------------ */

  var REGISTRY = [
    /* ---- Homepage ---- */
    { sel: '.egj-series__head', anim: 'rise' },
    { sel: '.egj-series-card', anim: 'fade-up' },
    { sel: '.egj-pergola__inner, .egj-pergola__preview', anim: 'fade-up' },
    { sel: '.fp-compare__header', anim: 'rise' },
    { sel: '.fp-compare__table', anim: 'fade-up' },
    { sel: '.fp-newsletter__inner', anim: 'fade-up' },
    { sel: '.fp-reviews__layout, .fp-reviews__list', anim: 'fade-up', stagger: true },
    { sel: '.egj-backyard-setups__grid', anim: 'fade-up', stagger: true },
    { sel: '.egj-marketing-panel', anim: 'fade-up' },
    { sel: '.collection-list', anim: 'fade-up', stagger: true },
    { sel: '.multicolumn-list', anim: 'fade-up', stagger: true },
    { sel: '.multicolumn .title-wrapper-with-link', anim: 'fade-up' },
    { sel: '.rich-text__blocks', anim: 'fade-up' },
    { sel: '.image-with-text__media, .image-with-text__content', anim: 'fade-up' },
    { sel: '.collapsible-content .collapsible-content__header, .collapsible-content__rows', anim: 'fade-up' },
    { sel: '.banner__box, .banner__content .banner__box', anim: 'fade-up' },

    /* ---- Collection / search shop ---- */
    { sel: '.collection-hero__text-wrapper', anim: 'fade-up' },
    { sel: '.egj-shop__controls', anim: 'fade' },
    { sel: '.egj-shop__empty', anim: 'fade-up' },
    { sel: '.collection--empty .title-wrapper', anim: 'fade-up' },
    { sel: '.pagination-wrapper', anim: 'fade' },

    /* ---- Product (buy box + editorial blocks) ---- */
    { sel: '.product__media-wrapper', anim: 'fade' },
    { sel: '.product__info-wrapper', anim: 'fade-up' },
    { sel: '.product__accordion', anim: 'fade-up' },
    { sel: '.egj-pdp-benefits__grid', anim: 'fade-up', stagger: true },
    /* Full product descriptions can exceed the viewport; keep them readable without a reveal gate. */
    { sel: '.egj-pdp-included__head', anim: 'rise' },
    { sel: '.egj-pdp-included__grid', anim: 'fade-up', stagger: true },
    { sel: '.egj-pdp-entertain__media, .egj-pdp-entertain__copy', anim: 'fade-up' },
    { sel: '.egj-pdp-specs__media, .egj-pdp-specs__panel', anim: 'fade-up' },
    { sel: '.egj-choose-finish__head', anim: 'rise' },
    { sel: '.egj-choose-finish__grid', anim: 'fade-up', stagger: true },
    { sel: '.egj-pdp-space__content', anim: 'fade-up' },
    { sel: '.egj-video-gallery__header', anim: 'rise' },
    { sel: '.egj-video-gallery__grid', anim: 'fade-up', stagger: true },
    { sel: '.egj-recent__inner', anim: 'fade-up' },
    { sel: '.product-recommendations, .related-products', anim: 'fade-up' },
    { sel: '.cs-body-wrapper', anim: 'fade-up' },

    /* ---- Cart ---- */
    { sel: '.fp-cart-page > .title-wrapper-with-link', anim: 'fade-up' },
    { sel: '.cart__items, .cart__footer', anim: 'fade' },
    { sel: '.cart__empty-text, .cart__warnings', anim: 'fade-up' },

    /* ---- Search ---- */
    { sel: '.search__input-wrapper, .template-search__header', anim: 'fade-up' },
    { sel: '.template-search__results', anim: 'fade' },

    /* ---- Design service / studio ---- */
    { sel: '.egj-ds__hero-inner', anim: 'fade-up' },
    { sel: '.egj-ds__facts', anim: 'fade-up', stagger: true },
    { sel: '.egj-ds__head', anim: 'fade-up' },
    { sel: '.egj-ds__rows', anim: 'fade-up', stagger: true },
    { sel: '.egj-ds__steps', anim: 'fade-up', stagger: true },
    { sel: '.egj-ds__index', anim: 'fade-up', stagger: true },
    { sel: '.egj-ds__rte, .egj-dsq__intro', anim: 'fade-up' },
    { sel: '.egj-dsq__fieldset', anim: 'fade-up' },
    { sel: '.egj-pergola__iframe-wrap', anim: 'fade' },

    /* ---- Content / legal / locations / contact ---- */
    { sel: '.egj-legal__hero-inner', anim: 'fade-up' },
    { sel: '.egj-legal__body', anim: 'fade' },
    { sel: '.egj-locations__hero-inner', anim: 'fade-up' },
    { sel: '.egj-locations__toolbar', anim: 'fade' },
    { sel: '.egj-locations__card, .egj-location-card', anim: 'fade-up' },
    { sel: '.egj-locations__footer', anim: 'fade-up' },
    /* Contact: hero + columns separately (not the body wrapper — nesting
       would collapse aside/form into a single block reveal). */
    { sel: '.egj-contact__hero-inner', anim: 'fade-up' },
    { sel: '.egj-contact__aside', anim: 'fade-up' },
    { sel: '.egj-contact__form-panel', anim: 'fade-up' },
    // Scoped to page templates on purpose: a bare .rte selector matches
    // rich-text inside sections that already own their reveal (fp-story et al)
    // and would double-gate them.
    { sel: '.fp-page__header, .fp-page__body, .fp-page > .rte', anim: 'fade' },
    { sel: '.warranty-form, .distributorship-form', anim: 'fade-up' },
    { sel: '.clearance-wrapper', anim: 'fade-up' },

    /* ---- Blog / article ---- */
    { sel: '.fp-blog__header, .fp-list-collections__header', anim: 'fade-up' },
    { sel: '.blog-articles', anim: 'fade-up', stagger: true },
    { sel: '.article-template__hero-container', anim: 'fade' },
    { sel: '.article-template__title, .article-template__content', anim: 'fade-up' },
    { sel: '.article-template__comment-wrapper', anim: 'fade' },

    /* ---- 404 / customer ---- */
    { sel: '.template-404', anim: 'fade-up' },
    { sel: '.customer', anim: 'fade-up' },

    /* ---- Footer ---- */
    { sel: '.fp-footer__grid', anim: 'fade-up', stagger: true },
  ];

  /* Never reveal-gate these: they are (or contain) the LCP element, or they
     are chrome that must be instantly usable. */
  /* Site chrome + the LCP hero. Deliberately NOT a bare `header` selector:
     content sections use semantic <header> elements too, and excluding those
     silently killed reveals on locations / blog / editorial pages. */
  var EXCLUDE_ANCESTORS =
    '.fp-hero, #shopify-section-header, .section-header, .header-main-wrapper, .app-che-header-container, .fp-utility-bar, .announcement-bar, .cart-drawer, .menu-drawer, .fp-mobile-nav, .fp-mobile-bottom-nav, predictive-search';

  function matchesExcluded(el) {
    try {
      return el.matches(EXCLUDE_ANCESTORS) || !!el.closest(EXCLUDE_ANCESTORS);
    } catch (e) {
      return false;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Marking                                                             */
  /* ------------------------------------------------------------------ */

  function markSection(el, childSel, staggerSel) {
    if (!el) return;
    el.classList.add('fp-reveal');
    el.setAttribute('data-fp-reveal', '');
    if (childSel) {
      var child = el.querySelector(childSel);
      if (child) child.classList.add('fp-reveal__child');
    }
    if (staggerSel) {
      var stagger = el.querySelector(staggerSel);
      if (stagger) stagger.classList.add('fp-reveal-stagger');
    }
  }

  function prepareAutoSections() {
    AUTO_SECTIONS.forEach(function (cfg) {
      document.querySelectorAll(cfg.root).forEach(function (el) {
        if (el.hasAttribute('data-fp-reveal')) {
          if (cfg.stagger) {
            var stagger = el.querySelector(cfg.stagger);
            if (stagger) stagger.classList.add('fp-reveal-stagger');
          }
          return;
        }
        markSection(el, cfg.child, cfg.stagger);
      });
    });
  }

  function indexStagger(el) {
    var max = 8;
    Array.prototype.forEach.call(el.children, function (child, i) {
      child.style.setProperty('--fp-i', String(Math.min(i, max)));
    });
  }

  /* Applies the registry. Idempotent: an element already carrying
     [data-fp-anim] is skipped, so re-running after section render is safe. */
  function prepareRegistry(scope) {
    var host = scope || document;
    REGISTRY.forEach(function (cfg) {
      var nodes;
      try {
        nodes = host.querySelectorAll(cfg.sel);
      } catch (e) {
        return;
      }
      Array.prototype.forEach.call(nodes, function (el) {
        if (el.hasAttribute('data-fp-anim')) return;
        if (matchesExcluded(el)) return;
        // Never nest a reveal inside another one: opacities would multiply and
        // the inner node would wait on the outer node's transition.
        if (el.parentElement && el.parentElement.closest('[data-fp-anim], [data-fp-stagger], .fp-reveal[data-fp-reveal]')) return;
        el.setAttribute('data-fp-anim', cfg.anim || 'fade-up');
        if (cfg.stagger && el.children.length) {
          el.setAttribute('data-fp-stagger', '');
          indexStagger(el);
        }
      });
    });

    /* Sections that declare [data-fp-stagger] in their own markup still need
       --fp-i on the children, otherwise every child animates on the same
       frame and the stagger reads as a single block. */
    Array.prototype.forEach.call(host.querySelectorAll('[data-fp-stagger]'), function (el) {
      if (!el.children.length) return;
      if (el.firstElementChild.style.getPropertyValue('--fp-i') !== '') return;
      indexStagger(el);
    });
  }

  function allTargets(scope) {
    var host = scope || document;
    return Array.prototype.slice.call(
      host.querySelectorAll('.fp-reveal[data-fp-reveal], [data-fp-anim]')
    );
  }

  function revealAll(nodes) {
    nodes.forEach(function (el) {
      el.classList.add('is-inview');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Observer                                                            */
  /* ------------------------------------------------------------------ */

  var observer = null;

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    return observer;
  }

  function observeReveals(scope) {
    var nodes = allTargets(scope);
    if (!nodes.length) return;

    if (reduce.matches || !('IntersectionObserver' in window)) {
      revealAll(nodes);
      return;
    }

    var io = getObserver();

    nodes.forEach(function (el) {
      if (el.classList.contains('is-inview')) return;
      var rect = el.getBoundingClientRect();
      // Already on screen at init → show immediately, never animate. This is
      // what keeps the LCP element out of the transition path.
      if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) {
        el.classList.add('is-inview');
        return;
      }
      io.observe(el);
    });
  }

  function bindReduceEscape() {
    if (typeof reduce.addEventListener !== 'function') return;
    reduce.addEventListener('change', function (e) {
      if (!e.matches) return;
      root.classList.remove('fp-motion-ready');
      revealAll(allTargets());
      if (observer) observer.disconnect();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky header                                                       */
  /* ------------------------------------------------------------------ */

  function bindHeaderScroll() {
    if (reduce.matches) return;

    var ticking = false;
    var threshold = 12;

    function update() {
      ticking = false;
      if (window.scrollY > threshold) {
        root.classList.add('html-scrolled');
      } else {
        root.classList.remove('html-scrolled');
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Micro-interactions                                                  */
  /* ------------------------------------------------------------------ */

  /* NOTE: image load fade-in is deliberately NOT implemented here. The theme
     already owns it end-to-end via egj-loading.js + the .egj-lazy-img rules in
     fp-collection.css / fp-pdp.css / fp-cart.css, which cover cards, the PDP
     gallery, cart thumbs and recommendations. Adding a second opacity layer
     here fought those rules and left card images transparent. */

  /* NOTE: no cart-count bump. fp-header.css / fp-kill-bbq.css deliberately kill
     animation on .cart-count-bubble with !important to fix an earlier bouncing
     regression, so the bump is left out rather than stacking another override.
     See the matching note in fp-motion.css. */

  /* Tag collapsibles so the accordion content fade applies. We only add a data
     hook — summary/aria semantics and Dawn's own toggling are untouched. */
  function bindAccordions(scope) {
    var host = scope || document;
    var nodes = host.querySelectorAll(
      '.accordion details, details.accordion, .product__accordion details, .collapsible-content details, details.fp-pdp-inquiry, .egj-legal details'
    );
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.hasAttribute('data-fp-accordion')) return;
      // Nav dropdowns, the header search disclosure and the drawers are also
      // <details>. They own their open/close motion — never touch them.
      if (el.closest(EXCLUDE_ANCESTORS + ', .fp-desktop-nav, header-menu')) return;
      el.setAttribute('data-fp-accordion', '');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  /* iOS Safari only applies :active (the press feedback in gk-mobile.css)
     once a touch listener exists on the page. */
  document.addEventListener('touchstart', function () {}, { passive: true });

  function prepareAll(scope) {
    prepareAutoSections();
    prepareRegistry(scope);
    bindAccordions(scope);
  }

  function init() {
    prepareAll();

    if (reduce.matches) {
      revealAll(allTargets());
      bindReduceEscape();
      return;
    }

    // Order matters: mark on-screen nodes first, THEN enable the hidden state.
    observeReveals();
    root.classList.add('fp-motion-ready');
    bindHeaderScroll();
    bindReduceEscape();
  }

  /* Re-run after async DOM swaps (facets, section render, quick add). */
  function refresh(scope) {
    prepareAll(scope);
    if (reduce.matches) {
      revealAll(allTargets(scope));
      return;
    }
    observeReveals(scope);
  }

  document.addEventListener('shopify:section:load', function (e) {
    refresh(e && e.target ? e.target : null);
  });
  document.addEventListener('facet:updated', function () {
    refresh();
  });

  /* Several regions are rendered by their own scripts after this one runs
     (locations grid, product recommendations, predictive search, the facet
     grid). One pass on window.load catches them without polling. */
  window.addEventListener('load', function () {
    refresh();
    watchDynamicHosts();
  });

  /* …and watch the known dynamic hosts for later swaps. Attached both now and
     on load, because several of these hosts are themselves injected late. */
  var DYNAMIC_HOSTS = [
    '#ProductGridContainer',
    '.egj-locations__grid',
    '.product-recommendations',
    '.egj-recent',
    '[data-egj-recently-viewed]',
  ];

  function watchDynamicHosts() {
    if (typeof MutationObserver === 'undefined') return;
    DYNAMIC_HOSTS.forEach(function (sel) {
      var host = document.querySelector(sel);
      if (!host || host.dataset.fpWatched === '1') return;
      host.dataset.fpWatched = '1';
      var mo = new MutationObserver(function () {
        window.clearTimeout(mo._t);
        mo._t = window.setTimeout(function () {
          refresh(host);
        }, 60);
      });
      mo.observe(host, { childList: true, subtree: false });
    });
  }

  watchDynamicHosts();

  // Expose a tiny hook so other scripts can re-arm motion after they inject DOM.
  window.fpMotion = { refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
